'use client';
import { leveransdagar } from '@/lib/leveranstid';
import { translateProductName } from '@/lib/product-translations';
import { visaModell } from '@/lib/fordonsnamn';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Link } from '@/components/LocaleLink';
import Image from 'next/image';
import { Search, Loader2, ArrowRight, Car, X, ChevronLeft } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useLocalePath } from '@/components/LocaleLink';
import { useCartStore } from '@/stores/cart';
import { useVehicleStore } from '@/stores/vehicle';
import { useCustomerTypeStore } from '@/stores/customer-type';
import { formatPrice, getDisplayPrice } from '@/lib/price';
import { usePriceFormatter } from '@/hooks/usePriceFormatter';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001/api';

function looksLikeVin(q: string): boolean {
  return /^[A-HJ-NPR-Z0-9]{17}$/i.test(q.replace(/\s+/g, ''));
}

interface VinVehicle {
  ktype: number;
  manufacturer: string;
  model: string;
  cartype: string;
  from_year: number;
  to_year: number | null;
}

interface VinCategory {
  id: number;
  name: string;
  slug: string;
  product_count: number;
}

interface SearchResult {
  id: number;
  reference: string;
  name: string;
  slug?: string | null;
  price: number;
  campaign_price?: number;
  campaign_discount_value?: number;
  quantity: number;
  allow_backorder: boolean;
  manufacturer_name?: string;
  images: { external_url: string; cover: boolean }[];
  key_features?: Record<string, string> | null;
}

export function SearchBar({ className, placeholder, mobile, onVehicleClick, inputId, showSubmit }: {
  className?: string;
  placeholder?: string;
  mobile?: boolean;
  /** Kopplar en <label> till fältet. Utan den läser skärmläsaren bara
   *  platshållaren, som försvinner så fort man skriver. */
  inputId?: string;
  /**
   * Synlig sökknapp. Utan den går sökningen bara att skicka med Enter — och på
   * en mobil utan fysiskt tangentbord är det inte uppenbart att det GÅR att
   * söka. Ägaren 2026-09-24: "det är inte uppenbart hur man söker".
   */
  showSubmit?: boolean;
  /** When set, the in-input vehicle chip becomes a tappable button that opens the
   *  vehicle selector — lets the header merge search + car-picker into one control. */
  onVehicleClick?: () => void;
}) {
  const [query, setQuery] = useState('');

  // "Välj bil"-tipset: den som börjar söka UTAN bil får en gång per session en
  // liten pil mot Välj bil-knappen (ägaren 2026-09-16). Stängs med kryss, av
  // sig själv efter åtta sekunder, eller när en bil väljs. Aldrig mer än en
  // gång per session — den som inte vill ha en bil ska inte tjatas på.
  const [visaBilTips, setVisaBilTips] = useState(false);
  /*
   * Chipet med bil/regskylt ligger ABSOLUT positionerat ovanpå fältet, så
   * fältet måste reservera plats åt det med padding. En fast klass duger inte:
   * `pr-20` (80 px) räckte inte för "GOC127" plus kryssknappen, och
   * platshållaren "Sök del till din bil – t.ex. bromsskiva" löpte rakt in i
   * skylten. Ägaren fotograferade det 2026-09-24 — texten gick inte att läsa.
   *
   * Bredden beror på regnumrets längd, språket och typsnittet, så den mäts i
   * stället för att gissas. 16 px marginal mellan text och chip.
   */
  const chipRef = useRef<HTMLDivElement | null>(null);
  const [chipBredd, setChipBredd] = useState(0);
  // Bubblan renderas via portal i <body>: sidhuvudet har backdrop-filter, och
  // inne i det ritades bubblan urblekt bakom sina syskon. Läget räknas från
  // sökrutans kant och följer med vid scroll och storleksändring.
  const [tipsLage, setTipsLage] = useState<{ top: number; left: number } | null>(null);
  useEffect(() => {
    if (!visaBilTips) { setTipsLage(null); return; }
    const uppdatera = () => {
      const r = wrapperRef.current?.getBoundingClientRect();
      if (r) setTipsLage({ top: r.top + r.height / 2, left: r.right + 16 });
    };
    uppdatera();
    window.addEventListener('resize', uppdatera);
    window.addEventListener('scroll', uppdatera, { passive: true });
    return () => { window.removeEventListener('resize', uppdatera); window.removeEventListener('scroll', uppdatera); };
  }, [visaBilTips]);
  useEffect(() => {
    if (!visaBilTips) return;
    const t = setTimeout(() => setVisaBilTips(false), 8000);
    return () => clearTimeout(t);
  }, [visaBilTips]);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [vinVehicle, setVinVehicle] = useState<VinVehicle | null>(null);
  const [vinCategories, setVinCategories] = useState<VinCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  // Löpnummer per sökning: ett äldre svar får inte skriva över ett nyare eller släcka dess snurra.
  const sokNr = useRef(0);
  const userTyped = useRef(false);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const selectedVehicle = useCartStore((state) => state.selectedVehicle);
  const clearCartVehicle = useCartStore((state) => state.clearVehicle);
  const clearStoreVehicle = useVehicleStore((state) => state.clearVehicle);
  const selectedPlate = useCartStore((state) => state.selectedPlate);
  const ktype = selectedVehicle?.ktype;

  /**
   * Ta bort vald bil. Fanns ingen väg alls tidigare: chippet i sökrutan kunde
   * bara BYTA bil, aldrig rensa den. Den som valt fel bil satt fast med ett
   * filter som tyst gömde allt annat.
   *
   * Rensar båda butikerna — varukorgen bär bilen för orderraderna, fordonsstoret
   * för navigering — och tar bort ktype ur URL:en när vi står på en sida som
   * läser den ur searchParams (/sok, /katalog). Annars skulle chippet försvinna
   * men träffarna förbli filtrerade.
   */
  const clearSelectedVehicle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearCartVehicle();
    clearStoreVehicle();
    if (searchParams?.get('ktype')) {
      const next = new URLSearchParams(searchParams.toString());
      next.delete('ktype');
      next.delete('page');
      router.push(`${pathname}?${next.toString()}`);
    } else if (pathname?.startsWith('/fordon/') || pathname?.startsWith('/en/fordon/')) {
      // Fordonssidan ÄR bilen — tar man bort bilen i sidhuvudet kan den inte
      // stå kvar därunder (ägaren 2026-09-16). Vidare till katalogen utan bil.
      router.push('/katalog');
    }
  };

  // Reflect the active search term in the box so the header search "follows"
  // what the customer searched (e.g. on /sok?q=bromsar the box shows "bromsar").
  // Prefill never opens the dropdown (only real typing does — see userTyped).
  useEffect(() => {
    // Gäller både /sok och /en/sok.
    if (pathname === '/sok' || pathname === '/en/sok') {
      setQuery(searchParams?.get('q') ?? '');
    }
  }, [pathname, searchParams]);
  const customerType = useCustomerTypeStore((state) => state.customerType);
  const { locale, currency } = useLocale();
  const localePath = useLocalePath();
  const fp = usePriceFormatter();

  const displayPrice = (price: number) =>
    currency === 'EUR' ? fp(price) : formatPrice(getDisplayPrice(price, customerType));

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (query.trim().length < 2) {
      sokNr.current++;
      setLoading(false);
      setResults([]);
      setVinVehicle(null);
      setVinCategories([]);
      setOpen(false);
      return;
    }

    // Snurran tänds först när sökningen faktiskt går iväg, inte vid varje tangent (2026-10-14).
    // Förra träfflistan ligger kvar tills de nya kommit.
    debounceRef.current = setTimeout(async () => {
      const nr = ++sokNr.current;
      setLoading(true);
      try {
        const trimmed = query.trim();
        const isVin = looksLikeVin(trimmed);

        const [productRes, vinRes] = await Promise.all([
          fetch(`${API_URL}/products/search?${new URLSearchParams({ q: trimmed, limit: '9', ...(ktype ? { ktype: String(ktype) } : {}) })}`)
            .then(r => r.ok ? r.json() : null)
            .catch(() => null),
          isVin
            ? fetch(`${API_URL}/regnum/vin?vin=${encodeURIComponent(trimmed.toUpperCase())}`)
                .then(r => r.ok ? r.json() : null)
                .catch(() => null)
            : Promise.resolve(null),
        ]);
        if (nr !== sokNr.current) return;

        const items = productRes
          ? (Array.isArray(productRes) ? productRes : productRes.data || []).slice(0, 9)
          : [];

        // Har besökaren valt bil visas ENDAST delar som passar den bilen.
        //
        // Tidigare fylldes listan på med ofiltrerade träffar när fordonsfiltret
        // gav färre än fem, märkta _vehicleMatch: false. Det gjorde en
        // fordonsfiltrerad sökning missvisande: man fick delar som inte passar
        // bilen man valt, i samma lista som dem som gör det. Borttaget
        // 2026-09-03 på ägarens begäran. Få eller inga träffar är ett ärligare
        // svar än fel träffar.
        setResults(items);
        setVinVehicle(vinRes?.vehicle || null);

        // Fetch categories for the VIN vehicle
        if (vinRes?.ktype) {
          fetch(`${API_URL}/categories/vehicle-summary?ktype=${vinRes.ktype}`)
            .then(r => r.ok ? r.json() : [])
            .then((cats: VinCategory[]) => {
              setVinCategories(cats.sort((a, b) => b.product_count - a.product_count).slice(0, 6));
            })
            .catch(() => setVinCategories([]));
        } else {
          setVinCategories([]);
        }

        if (userTyped.current) setOpen(true);
      } catch {
        if (nr !== sokNr.current) return;
        setResults([]);
        setVinVehicle(null);
        setVinCategories([]);
      } finally {
        if (nr === sokNr.current) setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, ktype]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    const el = chipRef.current;
    if (!el) { setChipBredd(0); return; }
    const mat = () => setChipBredd(el.offsetWidth);
    mat();
    // Skylten byts när kunden väljer en annan bil, och texten kan ändra bredd
    // när typsnittet laddat.
    const obs = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(mat) : null;
    obs?.observe(el);
    return () => obs?.disconnect();
  }, [ktype, selectedPlate, selectedVehicle?.manufacturer, onVehicleClick, locale]);

  const searchUrl = (q: string) => {
    const params = new URLSearchParams({ q });
    if (ktype) params.set('ktype', String(ktype));
    // Prefixas för engelska besökare. Sökrutan sitter i headern på varje sida,
    // så utan detta kastas besökaren ut ur /en/-trädet mitt i sessionen.
    return localePath(`/sok?${params}`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = query.trim();
    if (q) {
      setOpen(false);
      setQuery('');
      setResults([]);
      router.push(searchUrl(q));
    }
  };

  const handleSelect = (product: SearchResult) => {
    setOpen(false);
    setQuery('');
    router.push(localePath(`/produkt/${product.slug || product.id}`));
  };

  const coverImage = (images: SearchResult['images']) => {
    const cover = images.find((i) => i.cover) || images[0];
    return cover?.external_url;
  };

  const faltOchKnapp = showSubmit ? 'flex gap-2 items-stretch' : '';

  return (
    <div ref={wrapperRef} className={`relative ${className || ''}`}>
      <form onSubmit={handleSubmit} className={faltOchKnapp}>
        <div className="relative flex-1 min-w-0">
          {/* Snurran tar förstoringsglasets plats medan sökningen går. Förut satt den till höger och tog bort
              bilchipet en stund vid varje tangent — rutan hoppade (2026-10-14). */}
          {loading ? (
            <Loader2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          ) : (
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          )}
          <Input
            id={inputId}
            type="search"
            placeholder={
              // En given platshållare vinner (bilraden på fordonssidan skickar en kort);
              // annars bilens namn utan chassikod i parentes (ägaren 2026-09-16).
              placeholder
                ?? (selectedVehicle
                  ? t(locale, 'search.searchForVehicle', { manufacturer: selectedVehicle.manufacturer, model: visaModell(selectedVehicle.model) })
                  : t(locale, 'search.searchGeneric'))
            }
            /* bg-background med flit: den delade Input-komponenten är
               bg-transparent och tar färgen från det den ligger på. I den
               tonade sökrutan blev fältet därmed tonat och läste sig inte som
               ett fält — ägaren 2026-09-24: "borde inte rutan man skriver i
               vara vit?". Att ändra Input globalt hade träffat varje formulär
               på sajten. */
            className="pl-10 pr-4 bg-background"
            // Uppmätt bredd på chipet + 16 px luft. Faller tillbaka på de gamla
            // klasserna först vid rendering på servern, innan mätningen hunnit.
            style={chipBredd ? { paddingRight: chipBredd + 16 } : undefined}
            value={query}
            onChange={(e) => {
              userTyped.current = true;
              setQuery(e.target.value);
              if (onVehicleClick && !ktype && e.target.value.trim().length >= 2) {
                try {
                  if (!sessionStorage.getItem('af-valj-bil-tips')) {
                    sessionStorage.setItem('af-valj-bil-tips', '1');
                    setVisaBilTips(true);
                  }
                } catch { /* privat läge utan lagring: inget tips, inget fel */ }
              }
            }}
            onFocus={() => results.length > 0 && setOpen(true)}
          />
          {/* In-input vehicle chip. With onVehicleClick it's a tappable button that
              opens the vehicle selector (merged search + car-picker for mobile). */}
          {onVehicleClick ? (
            <div ref={chipRef} className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center max-w-[52%]">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onVehicleClick(); }}
                className={`flex items-center gap-1 min-w-0 h-7 px-2 border bg-secondary/60 text-xs font-medium text-primary hover:bg-secondary transition-colors ${ktype ? 'rounded-l-md border-r-0' : 'rounded-md'}`}
                aria-label={t(locale, 'search.changeVehicle')}
              >
                <Car className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">
                  {ktype ? (selectedPlate ? selectedPlate.toUpperCase() : selectedVehicle?.manufacturer) : t(locale, 'search.selectVehicle')}
                </span>
              </button>
              {ktype && (
                <button
                  type="button"
                  onClick={clearSelectedVehicle}
                  className="flex items-center justify-center h-7 w-7 shrink-0 rounded-r-md border bg-secondary/60 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
                  aria-label={t(locale, 'search.clearVehicle')}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          ) : ktype ? (
            <span ref={chipRef as React.RefObject<HTMLSpanElement>} className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 text-xs font-medium text-primary max-w-[52%]">
              <Car className="h-3 w-3 shrink-0" />
              <span className="truncate">{selectedPlate ? selectedPlate.toUpperCase() : selectedVehicle?.manufacturer}</span>
              <button
                type="button"
                onClick={clearSelectedVehicle}
                className="flex items-center justify-center h-6 w-6 shrink-0 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                aria-label={t(locale, 'search.clearVehicle')}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ) : null}
        </div>
        {showSubmit && (
          <button
            type="submit"
            className="shrink-0 inline-flex items-center gap-1.5 rounded-md bg-brand px-4 text-sm
                       font-medium text-white hover:bg-brand-dark active:scale-[0.98] transition"
          >
            <Search className="h-4 w-4" aria-hidden />
            <span className="hidden sm:inline">{t(locale, 'search.submit')}</span>
            <span className="sm:hidden sr-only">{t(locale, 'search.submit')}</span>
          </button>
        )}
      </form>

      {visaBilTips && !ktype && onVehicleClick && tipsLage && typeof document !== 'undefined' && createPortal(
        <div
          role="status"
          className="af-biltips fixed z-[110] w-72 -translate-y-1/2 rounded-lg border border-amber-300 bg-amber-50 shadow-lg p-3 text-sm text-amber-900"
          style={{ top: tipsLage.top, left: tipsLage.left }}
        >
          <ChevronLeft className="af-biltips-pil absolute -left-3.5 top-1/2 h-5 w-5 text-amber-500" aria-hidden />
          <div className="flex items-start gap-2">
            <Car className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
            <button
              type="button"
              onClick={() => { setVisaBilTips(false); onVehicleClick(); }}
              className="flex-1 text-left leading-snug hover:underline"
            >
              {t(locale, 'search.vehicleTip')}
            </button>
            <button
              type="button"
              onClick={() => setVisaBilTips(false)}
              aria-label={t(locale, 'common.close')}
              className="shrink-0 -mr-1 -mt-1 h-6 w-6 inline-flex items-center justify-center rounded-full text-amber-700 hover:bg-amber-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>,
        document.body,
      )}

      {/* Mega menu dropdown */}
      {open && query.trim().length >= 2 && (
        <div className={`absolute z-[100] mt-1 bg-background border rounded-lg shadow-xl overflow-y-auto overflow-x-hidden ${
          mobile
            ? 'left-0 right-0 max-h-[calc(100dvh-140px)]'
            : 'left-0 right-0 md:right-auto md:w-[min(700px,calc(100vw-2rem))] lg:w-[min(900px,calc(100vw-2rem))] max-h-[70vh]'
        }`}>

          {/*
            * Vägen till den riktiga träffsidan ligger HÖGST UPP, inte längst
            * ned. Tidigare visades "Visa alla resultat" bara vid nio träffar
            * och satt under en rullbar lista, så den som sökte via mobilens
            * Sök-knapp fick en panel med fyra varor och ingen synlig väg till
            * sidan där filtren, sorteringen och fordonsvalet finns. Ägaren
            * beskrev det 2026-09-24 som att sökningen saknar "filtrering eller
            * någon logik som vi har på produktsök överhuvudtaget" — och det
            * stämde för den vägen in, även om sidan bakom hade allt.
            */}
          {results.length > 0 && (
            <Link
              href={searchUrl(query.trim())}
              onClick={() => setOpen(false)}
              className="flex items-center justify-between gap-2 px-4 py-2.5 border-b bg-primary/5
                         text-sm font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <span>Visa alla träffar med filter och sortering</span>
              <ArrowRight className="h-4 w-4 shrink-0" />
            </Link>
          )}

          {/* Vehicle filter indicator */}
          {ktype && selectedVehicle && (
            <div className="flex items-center gap-2 px-4 py-2 bg-primary/5 border-b text-xs text-muted-foreground">
              <Car className="h-3 w-3 text-primary" />
              Filtrerat för {selectedVehicle.manufacturer} {visaModell(selectedVehicle.model)}
            </div>
          )}

          {/* VIN vehicle result with categories */}
          {vinVehicle && (
            <div className="border-b">
              <Link
                href={`/fordon/${vinVehicle.ktype}?vin=${query.trim().toUpperCase()}`}
                onClick={() => { setOpen(false); setQuery(''); }}
                className="flex items-center gap-3 px-4 py-3 bg-primary/5 hover:bg-primary/10 transition-colors"
              >
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <Car className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold">
                    {vinVehicle.manufacturer} {visaModell(vinVehicle.model)}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {vinVehicle.cartype} ({vinVehicle.from_year}{vinVehicle.to_year ? `–${vinVehicle.to_year}` : '+'})
                  </p>
                </div>
                <span className="text-xs font-medium text-primary flex items-center gap-1 shrink-0">
                  Alla delar <ArrowRight className="h-3 w-3" />
                </span>
              </Link>

              {/* Top categories for this vehicle */}
              {vinCategories.length > 0 && (
                <div className="px-4 py-2 bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-2">Populära kategorier för detta fordon:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {vinCategories.map((cat) => (
                      <Link
                        key={cat.id}
                        href={`/katalog/${cat.slug}?ktype=${vinVehicle.ktype}`}
                        onClick={() => { setOpen(false); setQuery(''); }}
                        className="px-2.5 py-1 bg-background border rounded-full text-xs hover:bg-accent hover:border-primary/30 transition-colors"
                      >
                        {cat.name}
                        <span className="ml-1 text-muted-foreground">({cat.product_count})</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {results.length > 0 ? (
            <>
              {/* Product grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border">
                {results.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => handleSelect(product)}
                    className="bg-background p-3 hover:bg-accent transition-colors text-left flex flex-col"
                  >
                    {/* Product image */}
                    <div className="aspect-[4/3] w-full rounded bg-muted overflow-hidden mb-2 relative">
                      {coverImage(product.images) ? (
                        <Image
                          src={coverImage(product.images)!}
                          alt={translateProductName(product.name)}
                          fill
                          sizes="(max-width: 768px) 40vw, 150px"
                          className="object-contain"
                          referrerPolicy="no-referrer"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const parent = target.parentElement;
                            if (parent && !parent.querySelector('.img-fallback')) {
                              const fb = document.createElement('div');
                              fb.className = 'img-fallback w-full h-full flex items-center justify-center text-muted-foreground text-xs absolute inset-0';
                              fb.textContent = 'Ingen bild';
                              parent.appendChild(fb);
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-muted-foreground text-xs">
                          Ingen bild
                        </div>
                      )}
                    </div>

                    {/* Product info */}
                    <p className="text-sm font-medium line-clamp-2 leading-tight">{translateProductName(product.name)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {product.reference}
                    </p>
                    {product.manufacturer_name && (
                      <p className="text-xs text-muted-foreground">{product.manufacturer_name}</p>
                    )}
                    {product.key_features && (() => {
                      const kf = product.key_features;
                      const specs: string[] = [];
                      if (kf['Ytterdiameter [mm]']) specs.push(`Ø${kf['Ytterdiameter [mm]']}mm`);
                      if (kf['Diameter (mm)']) specs.push(`Ø${kf['Diameter (mm)']}mm`);
                      const len = kf['Längd[mm]'] || (kf['Längd (cm)'] ? `${parseFloat(kf['Längd (cm)']) * 10}` : null);
                      if (len) specs.push(`${len}mm`);
                      if (kf['Tjocklek [mm.]']) specs.push(`${kf['Tjocklek [mm.]']}mm`);
                      if (kf['Spänning [V]']) specs.push(`${kf['Spänning [V]']}V`);
                      if (kf['Bromsskivetyp']) specs.push(kf['Bromsskivetyp']);
                      return specs.length > 0 ? (
                        <p className="text-[10px] text-muted-foreground truncate">{specs.slice(0, 2).join(' · ')}</p>
                      ) : null;
                    })()}

                    {/* Price */}
                    {product.price > 0 && (
                      <div className="mt-auto pt-1.5">
                        {product.campaign_price ? (
                          <div className="flex items-baseline gap-1.5">
                            <span className="text-sm font-bold text-red-600">
                              {displayPrice(product.campaign_price)}
                            </span>
                            <span className="text-xs text-muted-foreground line-through">
                              {displayPrice(product.price)}
                            </span>
                          </div>
                        ) : (
                          <p className="text-sm font-bold">
                            {displayPrice(product.price)}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Delivery indicator */}
                    <span className="text-[10px] text-green-700 font-medium">Levereras {leveransdagar(product)} arbetsdagar</span>
                  </button>
                ))}
              </div>

              {/* Samma väg igen för den som rullat hela listan. Tröskeln på nio
                  träffar är borttagen: antalet vi råkar visa säger ingenting om
                  hur många som finns, och kunden ska inte behöva gissa. */}
              {results.length > 0 && (
                <Link
                  href={searchUrl(query.trim())}
                  onClick={() => setOpen(false)}
                  className="flex items-center justify-center gap-2 p-3 text-sm text-primary hover:bg-accent transition-colors font-medium border-t"
                >
                  {t(locale, 'search.showAllResults')}
                  <ArrowRight className="h-4 w-4" />
                </Link>
              )}
            </>
          ) : !vinVehicle ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {t(locale, 'search.noProductsFor', { query })}
              {ktype && selectedVehicle && (
                <span> som passar {selectedVehicle.manufacturer} {visaModell(selectedVehicle.model)}</span>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
